/**
 * ManualAddressForm Component
 * Manual address entry form as fallback for address lookup
 */

import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { ADDRESS_FIELDS, DEFAULTS } from '../utils/addressConstants';

/**
 * ManualAddressForm Component
 * @param {Object} props - Component props
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.onAddressChange - Address change handler
 * @param {Function} props.onSwitchToLookup - Switch back to lookup handler
 * @param {boolean} props.showSwitchOption - Whether to show switch to lookup option
 * @param {string} props.className - Additional CSS classes
 */
const ManualAddressForm = ({
  initialValues = {},
  onAddressChange,
  onSwitchToLookup,
  showSwitchOption = true,
  className = ''
}) => {
  const [formData, setFormData] = useState({
    [ADDRESS_FIELDS.LINE1]: '',
    [ADDRESS_FIELDS.LINE2]: '',
    [ADDRESS_FIELDS.CITY]: '',
    [ADDRESS_FIELDS.COUNTY]: '',
    [ADDRESS_FIELDS.POSTCODE]: '',
    [ADDRESS_FIELDS.COUNTRY]: DEFAULTS.COUNTRY,
    ...initialValues
  });

  /**
   * Handle input change
   */
  const handleInputChange = (field, value) => {
    const updatedFormData = {
      ...formData,
      [field]: value
    };
    
    setFormData(updatedFormData);
    
    // Notify parent of changes
    if (onAddressChange) {
      // Include legacy address field for backward compatibility
      const legacyAddress = buildLegacyAddress(updatedFormData);
      onAddressChange({
        ...updatedFormData,
        [ADDRESS_FIELDS.LEGACY]: legacyAddress
      });
    }
  };

  /**
   * Build legacy address string from structured fields
   */
  const buildLegacyAddress = (data) => {
    const parts = [];
    
    if (data[ADDRESS_FIELDS.LINE1]) parts.push(data[ADDRESS_FIELDS.LINE1]);
    if (data[ADDRESS_FIELDS.LINE2]) parts.push(data[ADDRESS_FIELDS.LINE2]);
    if (data[ADDRESS_FIELDS.CITY]) parts.push(data[ADDRESS_FIELDS.CITY]);
    if (data[ADDRESS_FIELDS.COUNTY]) parts.push(data[ADDRESS_FIELDS.COUNTY]);
    if (data[ADDRESS_FIELDS.POSTCODE]) parts.push(data[ADDRESS_FIELDS.POSTCODE]);
    
    return parts.join(', ');
  };

  /**
   * Handle postcode formatting
   */
  const handlePostcodeChange = (value) => {
    // Auto-format UK postcode
    const formatted = value.toUpperCase().replace(/\s+/g, '').replace(/^(.+)(\d[A-Z]{2})$/, '$1 $2');
    handleInputChange(ADDRESS_FIELDS.POSTCODE, formatted);
  };

  /**
   * Update form when initial values change
   */
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialValues
      }));
    }
  }, [initialValues]);

  return (
    <div className={`w-full ${className}`}>
      <div className="flex flex-col gap-4">
        {/* Address Line 1 */}
        <div className="flex flex-col">
          <label 
            htmlFor="address-line1" 
            className="font-medium mb-1 text-gray-700 text-sm"
          >
            Address Line 1
          </label>
          <input
            id="address-line1"
            type="text"
            value={formData[ADDRESS_FIELDS.LINE1]}
            onChange={(e) => handleInputChange(ADDRESS_FIELDS.LINE1, e.target.value)}
            placeholder="Building number and street name"
            className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 placeholder-gray-400"
            autoComplete="address-line1"
          />
        </div>

        {/* Address Line 2 */}
        <div className="flex flex-col">
          <label 
            htmlFor="address-line2" 
            className="font-medium mb-1 text-gray-700 text-sm"
          >
            Address Line 2 <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <input
            id="address-line2"
            type="text"
            value={formData[ADDRESS_FIELDS.LINE2]}
            onChange={(e) => handleInputChange(ADDRESS_FIELDS.LINE2, e.target.value)}
            placeholder="Apartment, suite, unit, etc."
            className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 placeholder-gray-400"
            autoComplete="address-line2"
          />
        </div>

        {/* City and County Row */}
        <div className="flex gap-4 sm:flex-row flex-col">
          <div className="flex flex-col flex-1">
            <label 
              htmlFor="address-city" 
              className="font-medium mb-1 text-gray-700 text-sm"
            >
              Town/City
            </label>
            <input
              id="address-city"
              type="text"
              value={formData[ADDRESS_FIELDS.CITY]}
              onChange={(e) => handleInputChange(ADDRESS_FIELDS.CITY, e.target.value)}
              placeholder="Town or city"
              className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 placeholder-gray-400"
              autoComplete="address-level2"
            />
          </div>

          <div className="flex flex-col flex-1">
            <label 
              htmlFor="address-county" 
              className="font-medium mb-1 text-gray-700 text-sm"
            >
              County <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              id="address-county"
              type="text"
              value={formData[ADDRESS_FIELDS.COUNTY]}
              onChange={(e) => handleInputChange(ADDRESS_FIELDS.COUNTY, e.target.value)}
              placeholder="County"
              className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 placeholder-gray-400"
              autoComplete="address-level1"
            />
          </div>
        </div>

        {/* Postcode and Country Row */}
        <div className="flex gap-4 sm:flex-row flex-col">
          <div className="flex flex-col flex-1">
            <label 
              htmlFor="address-postcode" 
              className="font-medium mb-1 text-gray-700 text-sm"
            >
              Postcode
            </label>
            <input
              id="address-postcode"
              type="text"
              value={formData[ADDRESS_FIELDS.POSTCODE]}
              onChange={(e) => handlePostcodeChange(e.target.value)}
              placeholder="SW1A 1AA"
              className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 placeholder-gray-400"
              autoComplete="postal-code"
              maxLength={8}
            />
          </div>

          <div className="flex flex-col flex-1">
            <label 
              htmlFor="address-country" 
              className="font-medium mb-1 text-gray-700 text-sm"
            >
              Country
            </label>
            <select
              id="address-country"
              value={formData[ADDRESS_FIELDS.COUNTRY]}
              onChange={(e) => handleInputChange(ADDRESS_FIELDS.COUNTRY, e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 cursor-pointer"
              autoComplete="country"
            >
              <option value="United Kingdom">United Kingdom</option>
              <option value="Ireland">Ireland</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualAddressForm;